import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import { OrgFinanceReportRow, OrgReportsApi } from '@/services/org-reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatRupees(n: number): string {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Finance Report — org tier, read-only. Cross-school income/expense/
 * balance, matching the Departments/Academic Calendar report pattern.
 * Session Year is a per-school lookup (academic_years lives in each
 * school's own schema), so it's disabled until a specific school is
 * picked — same cascade as the Academic Calendar report. Real data is
 * genuinely thin platform-wide (9 income rows / 114 expense rows,
 * confirmed live) — most schools show zero rows here; that's expected,
 * not broken, same as school-tier.
 */
export function OrgFinanceReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-finance-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: years = [] } = useQuery({
    queryKey: ['org-finance-report-years', schoolId],
    queryFn: () => OrgPromotionApi.academicYearsForSchool(schoolId),
    enabled: !!schoolId,
  });

  const params = {
    schoolId: schoolId || undefined,
    academicYearId: (schoolId && academicYearId) || undefined,
  };

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-finance-report', params],
    queryFn: () => OrgReportsApi.financeReport(params),
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportFinanceReport(format, params);
    } finally {
      setBusy(null);
    }
  };

  function handleSchoolChange(v: string) {
    setSchoolId(v);
    setAcademicYearId('');
  }

  const totals = rows.reduce(
    (acc, r) => ({ income: acc.income + r.totalIncome, expense: acc.expense + r.totalExpense, balance: acc.balance + r.balance }),
    { income: 0, expense: 0, balance: 0 },
  );

  return (
    <>
      <PageHeader
        title="Finance Report"
        description="Income, expense, and balance by session year and month, across your organization's schools."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={schoolId} onChange={(e) => handleSchoolChange(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-44"
          value={academicYearId}
          onChange={(e) => setAcademicYearId(e.target.value)}
          disabled={!schoolId}
          title={schoolId ? undefined : 'Select a school to filter by session year'}
        >
          <option value="">All Years</option>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Year</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Month</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total Income</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total Expense</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                    No income or expense entries for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r: OrgFinanceReportRow, i) => (
                  <tr key={`${r.schoolId}-${r.academicYearId}-${r.month}-${i}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.schoolName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.academicYearName ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{MONTH_NAMES[r.month - 1] ?? r.month}</td>
                    <td className="px-4 py-3 text-right text-slate-900">{formatRupees(r.totalIncome)}</td>
                    <td className="px-4 py-3 text-right text-slate-900">{formatRupees(r.totalExpense)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${r.balance < 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {formatRupees(r.balance)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-slate-50 font-semibold">
                <tr>
                  <td className="px-4 py-3 text-slate-900" colSpan={3}>
                    Total
                  </td>
                  <td className="px-4 py-3 text-right text-slate-900">{formatRupees(totals.income)}</td>
                  <td className="px-4 py-3 text-right text-slate-900">{formatRupees(totals.expense)}</td>
                  <td className={`px-4 py-3 text-right ${totals.balance < 0 ? 'text-red-600' : 'text-green-700'}`}>
                    {formatRupees(totals.balance)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
}
